import {
  COST_AVOIDANCE_FOR_RANGE_QUERY_KEY,
  ELECTRICITY_QUERY_KEY,
  SCHEDULE_QUERY_KEY,
  StockDevelopmentQueryKey,
  useDeleteScheduleItemMutation
} from '@domain/schedule'
import type {ScheduleItem, Schedule} from '@domain/schedule'
import {useUpmPlantContext} from '@hconnect/upm'
import {spawnErrorToast} from '@hm/fundamental'
import {useQueryClient} from '@tanstack/react-query'
import {Moment} from 'moment-timezone'
import {useCallback, useMemo} from 'react'
import {useErrorHandler} from 'react-error-boundary'

import {useTrackPlannerEvent} from '../../../helpers'

type ScheduleContext = {previousState: Schedule}
interface DeleteScheduleItemParams {
  startOfPlan: Moment
  endOfPlan: Moment
}

export const useDeleteScheduleItem = ({startOfPlan, endOfPlan}: DeleteScheduleItemParams) => {
  const raiseError = useErrorHandler()
  const queryClient = useQueryClient()
  const {plantId: plantCode} = useUpmPlantContext()
  const queryVariables = useMemo(
    () => ({
      plantCode,
      start: startOfPlan.toISOString(),
      end: endOfPlan.toISOString()
    }),
    [plantCode, startOfPlan, endOfPlan]
  )
  const trackPlannerEvent = useTrackPlannerEvent()

  const {mutate} = useDeleteScheduleItemMutation({
    onMutate: async (variables) => {
      await queryClient.cancelQueries({queryKey: [SCHEDULE_QUERY_KEY]})
      const previousState = queryClient.getQueryData<Schedule>([SCHEDULE_QUERY_KEY, queryVariables])
      const id = variables.scheduleItem.id
      if (previousState) {
        const updatedSchedule = {...previousState, schedules: {...previousState.schedules}}
        delete updatedSchedule.schedules[id]
        queryClient.setQueryData([SCHEDULE_QUERY_KEY, queryVariables], updatedSchedule)
      }
      trackPlannerEvent({name: 'onDeleteScheduleItem'})
      return {previousState} as ScheduleContext
    },
    onError: (error) => {
      const detail = (error.response?.data as {detail?: string} | undefined)?.detail
      if (typeof detail === 'string') spawnErrorToast(detail)
      else if (detail) raiseError(new Error(JSON.stringify(detail)))
      else raiseError(error)
    },
    onSettled: (data, error, {plantCode}) => {
      void queryClient.invalidateQueries({queryKey: [SCHEDULE_QUERY_KEY], refetchType: 'all'})
      void queryClient.invalidateQueries({
        queryKey: [ELECTRICITY_QUERY_KEY, {plantCode}],
        refetchType: 'all'
      })
      void queryClient.invalidateQueries({
        queryKey: [COST_AVOIDANCE_FOR_RANGE_QUERY_KEY],
        refetchType: 'all'
      })
      void queryClient.invalidateQueries({
        queryKey: [StockDevelopmentQueryKey],
        refetchType: 'all'
      })
    }
  })

  return useCallback(
    (scheduleItem: ScheduleItem) => {
      const schedule = queryClient.getQueryData<Schedule>([SCHEDULE_QUERY_KEY, queryVariables])
      if (!schedule) return
      mutate({plantCode, scheduleId: schedule.scheduleId, scheduleItem})
    },
    [mutate, queryVariables, plantCode, queryClient]
  )
}
